/**
 * Geo helpers for the "lat,lng" location strings stored on Store/Shop
 * (the dashboard's LocationPicker writes them in that exact format).
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/**
 * Parse `"24.860735,67.001073"` → { lat, lng }.
 * Returns null for missing/malformed/out-of-range input so callers can
 * filter entities that never had coordinates set.
 */
export function parseLatLng(location?: string | null): LatLng | null {
  if (!location || typeof location !== 'string') return null;
  const parts = location.split(',').map((p) => p.trim());
  if (parts.length !== 2) return null;
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  // "0,0" is the dashboard's untouched-map default — treat as unset.
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
}

const EARTH_RADIUS_KM = 6371;

/** Great-circle distance in kilometres (haversine). */
export function haversineKm(a: LatLng, b: LatLng): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** "850 m" under 1 km, "12.4 km" under 100, "120 km" beyond. */
export function formatDistance(km: number): string {
  if (!Number.isFinite(km) || km < 0) return '';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 100) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}
